import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  RefreshCw, Tv, Save, RotateCcw, Eye, EyeOff, Edit2, Trash2, 
  GripVertical, Clock, MapPin, User, AlertCircle, CheckCircle2,
  Play, Image as ImageIcon, Video, MessageSquare
} from 'lucide-react';
import { toast } from 'react-toastify';

function PlaylistPreview({ user }) {
  const [selectedLocation, setSelectedLocation] = useState('gym');
  const [playlist, setPlaylist] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkDuration, setBulkDuration] = useState(10);
  const [draggedItem, setDraggedItem] = useState(null);
  const [showRefreshDropdown, setShowRefreshDropdown] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const API_URL = import.meta.env.VITE_API_URL || window.location.origin;

  // Fetch playlist
  const fetchPlaylist = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/signage/playlist-preview/${selectedLocation}`);
      setPlaylist(response.data.items);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching playlist:', error);
      toast.error('Failed to load playlist');
    } finally {
      setLoading(false);
    }
  }, [selectedLocation, API_URL]);

  useEffect(() => {
    fetchPlaylist();
  }, [fetchPlaylist]);

  // ✅ Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showRefreshDropdown && !event.target.closest('.relative')) {
        setShowRefreshDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showRefreshDropdown]);

  // Toggle item visibility
  const toggleVisibility = async (item) => {
    try {
      await axios.put(`${API_URL}/api/signage/playlist-preview/${selectedLocation}/items/${item._id}`, {
        hidden: !item.hidden
      });
      
      toast.success(item.hidden ? 'Item shown' : 'Item hidden');
      fetchPlaylist();
    } catch (error) {
      console.error('Error toggling visibility:', error);
      toast.error('Failed to update visibility');
    }
  };

  // Update duration
  const updateDuration = async (item, newDuration) => {
    try {
      await axios.put(`${API_URL}/api/signage/playlist-preview/${selectedLocation}/items/${item._id}`, {
        durationOverride: parseInt(newDuration)
      });
      
      toast.success('Duration updated');
      fetchPlaylist();
    } catch (error) {
      console.error('Error updating duration:', error);
      toast.error('Failed to update duration');
    }
  };

  // Bulk hide selected
  const bulkHide = async () => {
    if (selectedItems.length === 0) {
      toast.warning('No items selected');
      return;
    }

    try {
      await axios.put(`${API_URL}/api/signage/playlist-preview/${selectedLocation}/bulk-update`, {
        itemIds: selectedItems,
        action: 'hide'
      });
      
      toast.success(`Hidden ${selectedItems.length} items`);
      setSelectedItems([]);
      fetchPlaylist();
    } catch (error) {
      console.error('Error hiding items:', error);
      toast.error('Failed to hide items');
    }
  };

  // Bulk show selected
  const bulkShow = async () => {
    if (selectedItems.length === 0) {
      toast.warning('No items selected');
      return;
    }

    try {
      await axios.put(`${API_URL}/api/signage/playlist-preview/${selectedLocation}/bulk-update`, {
        itemIds: selectedItems,
        action: 'show'
      });
      
      toast.success(`Shown ${selectedItems.length} items`);
      setSelectedItems([]);
      fetchPlaylist();
    } catch (error) {
      console.error('Error showing items:', error);
      toast.error('Failed to show items');
    }
  };

  // Bulk set duration
  const bulkSetDuration = async () => {
    if (selectedItems.length === 0) {
      toast.warning('No items selected');
      return;
    }

    try {
      await axios.put(`${API_URL}/api/signage/playlist-preview/${selectedLocation}/bulk-update`, {
        itemIds: selectedItems,
        action: 'setDuration',
        value: bulkDuration
      });
      
      toast.success(`Updated duration for ${selectedItems.length} items`);
      setSelectedItems([]);
      fetchPlaylist();
    } catch (error) {
      console.error('Error setting duration:', error);
      toast.error('Failed to set duration');
    }
  };

  // Reset to auto-generated
  const resetToAuto = async () => {
    if (!window.confirm(`Reset ${selectedLocation} playlist to auto-generated order? All customizations will be lost.`)) {
      return;
    }

    try {
      await axios.post(`${API_URL}/api/signage/playlist-preview/${selectedLocation}/reset`);
      toast.success('Playlist reset to auto-generated');
      fetchPlaylist();
    } catch (error) {
      console.error('Error resetting playlist:', error);
      toast.error('Failed to reset playlist');
    }
  };

  // Open TV preview
  const openTVPreview = () => {
    window.open(`/signage-display/${selectedLocation}`, '_blank');
    toast.info('Preview opened in new tab');
  };

  // Select/deselect item
  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Select all
  const selectAll = () => {
    if (selectedItems.length === playlist.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(playlist.map(item => item._id));
    }
  };

  // ✅ Refresh TV Display functions
  const refreshTVDisplay = async (location) => {
    setRefreshing(true);
    try {
      const response = await axios.post(`${API_URL}/api/signage/refresh-tv/${location}`);
      toast.success(response.data.message || `${location.charAt(0).toUpperCase() + location.slice(1)} TV refresh requested`);
      setShowRefreshDropdown(false);
    } catch (error) {
      console.error('Error refreshing TV:', error);
      toast.error('Failed to request TV refresh');
    } finally {
      setRefreshing(false);
    }
  };

  const refreshAllTVs = async () => {
    setRefreshing(true);
    try {
      const response = await axios.post(`${API_URL}/api/signage/refresh-tv/all/locations`);
      toast.success(response.data.message || 'All TVs refresh requested');
      setShowRefreshDropdown(false);
    } catch (error) {
      console.error('Error refreshing all TVs:', error);
      toast.error('Failed to request all TVs refresh');
    } finally {
      setRefreshing(false);
    }
  };

  // Render item icon
  const renderItemIcon = (item) => {
    if (item.type === 'media') {
      if (item.fileType === 'image') {
        return <ImageIcon className="h-5 w-5 text-blue-500" />;
      } else {
        return <Video className="h-5 w-5 text-purple-500" />;
      }
    } else {
      return <MessageSquare className="h-5 w-5 text-amber-500" />;
    }
  };

  // Get item title
  const getItemTitle = (item) => {
    if (item.type === 'media') {
      return item.fileName;
    } else {
      return item.title || 'Untitled Announcement';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold mb-2 text-gray-900">📺 Playlist Preview</h2>

        <p className="text-purple-100">See exactly what will play on your displays</p>
      </div>

      {/* Location & Actions */}
      <div className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm border">
        {/* Location Selector */}
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Location:</label>
          <div className="flex gap-2">
            {['gym', 'salon'].map((loc) => (
              <button
                key={loc}
                onClick={() => setSelectedLocation(loc)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedLocation === loc
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {loc.charAt(0).toUpperCase() + loc.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={fetchPlaylist}
            disabled={loading}
            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 flex items-center gap-2 text-sm font-medium transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh Data
          </button>
          
          {/* ✅ NEW: Refresh TV Display Button with Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowRefreshDropdown(!showRefreshDropdown)}
              disabled={refreshing}
              className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center gap-2 text-sm font-medium transition-colors"
            >
              <Tv className={`h-4 w-4 ${refreshing ? 'animate-pulse' : ''}`} />
              Refresh TV Display
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {/* Dropdown Menu */}
            {showRefreshDropdown && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-10">
                <div className="py-1">
                  <button
                    onClick={() => refreshTVDisplay('gym')}
                    disabled={refreshing}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Tv className="h-4 w-4 text-purple-600" />
                    Refresh Gym TV
                  </button>
                  <button
                    onClick={() => refreshTVDisplay('salon')}
                    disabled={refreshing}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Tv className="h-4 w-4 text-pink-600" />
                    Refresh Salon TV
                  </button>
                  <div className="border-t border-gray-200 my-1"></div>
                  <button
                    onClick={refreshAllTVs}
                    disabled={refreshing}
                    className="w-full text-left px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 flex items-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Refresh All TVs
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <button
            onClick={openTVPreview}
            className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 flex items-center gap-2 text-sm font-medium transition-colors"
          >
            <Tv className="h-4 w-4" />
            Test on TV
          </button>
          <button
            onClick={resetToAuto}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 text-sm font-medium transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            Reset to Auto
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-blue-900">📊 Total:</span>
              <span className="text-blue-700">{stats.totalItems} items</span>
            </div>
            <div className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4 text-blue-600" />
              <span className="text-blue-700">{stats.mediaCount} media</span>
            </div>
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-amber-600" />
              <span className="text-blue-700">{stats.announcementCount} announcements</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-600" />
              <span className="text-blue-700">{stats.totalDurationFormatted} loop</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-green-700">{stats.visibleCount} visible</span>
            </div>
            <div className="flex items-center gap-2">
              <EyeOff className="h-4 w-4 text-red-600" />
              <span className="text-red-700">{stats.hiddenCount} hidden</span>
            </div>
            {stats.itemsWithOverrides > 0 && (
              <div className="flex items-center gap-2">
                <Edit2 className="h-4 w-4 text-purple-600" />
                <span className="text-purple-700">{stats.itemsWithOverrides} customized</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      <div className="bg-gray-50 border rounded-lg p-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          {/* Selection */}
          <div className="flex items-center gap-3">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={selectedItems.length === playlist.length && playlist.length > 0}
                onChange={selectAll}
                className="mr-2 h-4 w-4 text-purple-600 rounded"
              />
              <span className="text-sm font-medium text-gray-700">
                {selectedItems.length > 0 ? `${selectedItems.length} selected` : 'Select All'}
              </span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={bulkHide}
              disabled={selectedItems.length === 0}
              className="px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded text-sm hover:bg-yellow-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Hide Selected
            </button>
            <button
              onClick={bulkShow}
              disabled={selectedItems.length === 0}
              className="px-3 py-1.5 bg-green-100 text-green-700 rounded text-sm hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Show Selected
            </button>
          </div>

          {/* Bulk Duration */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Set duration:</span>
            <input
              type="number"
              value={bulkDuration}
              onChange={(e) => setBulkDuration(parseInt(e.target.value) || 10)}
              className="border rounded px-2 py-1 w-16 text-sm"
              min="5"
              max="60"
            />
            <span className="text-sm text-gray-600">sec</span>
            <button
              onClick={bulkSetDuration}
              disabled={selectedItems.length === 0}
              className="px-3 py-1.5 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Playlist Items */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : playlist.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
            <AlertCircle className="h-12 w-12 mx-auto text-gray-400 mb-3" />
            <p className="text-lg font-medium text-gray-600">No items in playlist</p>
            <p className="text-sm text-gray-500 mt-2">Upload media or create announcements to get started</p>
          </div>
        ) : (
          playlist.map((item, index) => (
            <div
              key={item._id}
              className={`bg-white border-2 rounded-lg p-4 transition-all ${
                item.hidden 
                  ? 'border-red-200 bg-gray-50 opacity-60' 
                  : 'border-gray-200 hover:border-purple-300 hover:shadow-md'
              } ${selectedItems.includes(item._id) ? 'ring-2 ring-purple-400' : ''}`}
            >
              <div className="flex items-start gap-3">
                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={selectedItems.includes(item._id)}
                  onChange={() => toggleItemSelection(item._id)}
                  className="mt-1 h-4 w-4 text-purple-600 rounded"
                />

                {/* Number */}
                <div className="font-bold text-gray-700 w-8 mt-0.5">
                  {index + 1}.
                </div>

                {/* Content */}
                <div className="flex-1">
                  {/* Title Row */}
                  <div className="flex items-center gap-2 mb-2">
                    {renderItemIcon(item)}
                    <span className={`font-medium text-gray-900 ${item.hidden ? 'line-through' : ''}`}>
                      {getItemTitle(item)}
                    </span>
                    
                    {/* Badges */}
                    {item.hidden && (
                      <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded font-bold">
                        HIDDEN
                      </span>
                    )}
                    {item.hasOverrides && !item.hidden && (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded font-medium">
                        Customized
                      </span>
                    )}
                    {item.type === 'announcement' && item.template && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">
                        {item.template.charAt(0).toUpperCase() + item.template.slice(1)}
                      </span>
                    )}
                  </div>

                  {/* Controls Row */}
                  <div className="flex items-center gap-4 mb-2 flex-wrap">
                    {/* Duration */}
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-600">Duration:</span>
                      {item.fileType === 'video' ? (
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded font-mono">
                          {Math.floor(item.duration / 60)}m {item.duration % 60}s
                        </span>
                      ) : (
                        <select
                          value={item.duration}
                          onChange={(e) => updateDuration(item, e.target.value)}
                          className="border rounded px-2 py-1 text-sm"
                        >
                          {[5, 10, 15, 20, 25, 30, 40, 50, 60].map(sec => (
                            <option key={sec} value={sec}>{sec}s</option>
                          ))}
                        </select>
                      )}
                      {item.duration !== item.originalDuration && (
                        <span className="text-xs text-purple-600">
                          (was {item.originalDuration}s)
                        </span>
                      )}
                    </div>

                    {/* Visibility Toggle */}
                    <button
                      onClick={() => toggleVisibility(item)}
                      className={`px-3 py-1 rounded text-sm font-medium transition-colors flex items-center gap-1 ${
                        item.hidden
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                      }`}
                    >
                      {item.hidden ? (
                        <>
                          <Eye className="h-3 w-3" />
                          Show
                        </>
                      ) : (
                        <>
                          <EyeOff className="h-3 w-3" />
                          Hide
                        </>
                      )}
                    </button>
                  </div>

                  {/* Metadata Row */}
                  <div className="flex items-center gap-4 text-xs text-gray-600 flex-wrap">
                    {item.type === 'media' && (
                      <>
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {item.displayLocation.map(loc => loc.charAt(0).toUpperCase() + loc.slice(1)).join(', ')}
                        </div>
                        <div>{item.fileSizeFormatted}</div>
                      </>
                    )}
                    {item.type === 'announcement' && (
                      <>
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {item.displayLocation.map(loc => loc.charAt(0).toUpperCase() + loc.slice(1)).join(', ')}
                        </div>
                        <div>Priority: {item.priority || 5}</div>
                        <div>{item.displayMode || 'mixed'} mode</div>
                      </>
                    )}
                    {item.createdByName && (
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {item.createdByName}
                      </div>
                    )}
                    {item.createdAt && (
                      <div>
                        {new Date(item.createdAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>

                  {/* Hidden Warning */}
                  {item.hidden && (
                    <div className="mt-2 text-xs text-red-600 font-medium flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      This item won't play on TV
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Info Message */}
      {playlist.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-purple-900">
              <p className="font-medium mb-1">💡 Pro Tips:</p>
              <ul className="list-disc list-inside space-y-1 text-purple-800">
                <li>Changes save automatically when you edit duration or visibility</li>
                <li>Hidden items stay in your library - you can unhide them anytime</li>
                <li>Click "Test on TV" to preview changes before they go live</li>
                <li>Use bulk actions to quickly update multiple items at once</li>
                <li>Click "Reset to Auto" to remove all customizations and use auto-generated order</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlaylistPreview;